import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const mobileMenuVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const mobileLinkVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeInOut",
    },
  },
};

function Navbar() {
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 z-40 w-full flex items-center justify-between px-6 md:px-20 py-4 border-b border-gray-200 bg-gray-100 transition-transform duration-300 ${
          visible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        {/* Left: Desktop Links */}
        <div className="hidden md:flex items-center space-x-2">
          <Link
            to="/"
            className="text-sm text-black font-medium px-4 py-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="text-sm text-black font-medium px-4 py-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            About us
          </Link>
          <Link
            to="/benefits"
            className="text-sm text-black font-medium px-4 py-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            Benefits
          </Link>
        </div>

        {/* Left: Mobile Title */}
        <div className="md:hidden">
          <span className="text-2xl font-bold text-black">Mounjaro</span>
        </div>

        {/* Center: Desktop Title */}
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
          <span className="text-lg font-bold text-black">Mounjaro</span>
        </div>

        {/* Right: Desktop Contact Button */}
        <Link to="/contact" className="hidden md:flex items-center group">
          <span className="text-sm px-5 py-2 bg-gray-200 rounded-full shadow-sm transition-colors group-hover:bg-gray-300">
            Contact us
          </span>
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-200 transition-colors group-hover:bg-gray-300 ml-2">
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-700"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </div>
        </Link>

        {/* Right: Mobile Hamburger Menu Button */}
        <div className="md:hidden">
          <button onClick={() => setMobileMenuOpen(true)}>
            <svg
              className="w-8 h-8 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-gray-100 z-50 p-6 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-black">Mounjaro</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-12 h-12 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
              >
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <motion.div
              className="flex flex-col items-start space-y-6 mt-24"
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={mobileLinkVariants}>
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-6xl text-black font-medium"
                >
                  Home
                </Link>
              </motion.div>
              <motion.div variants={mobileLinkVariants}>
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-6xl text-black font-medium"
                >
                  About
                </Link>
              </motion.div>
              <motion.div variants={mobileLinkVariants}>
                <Link
                  to="/benefits"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-6xl text-black font-medium"
                >
                  Benefits
                </Link>
              </motion.div>
              <motion.div variants={mobileLinkVariants}>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-6xl text-black font-medium"
                >
                  Contact
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              className="mt-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <Link
                to="/contact"
                className="flex items-center group"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="text-xl px-8 py-4 bg-gray-200 rounded-full shadow-sm transition-colors group-hover:bg-gray-300 relative z-10">
                  Contact us
                </span>
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-200 transition-colors group-hover:bg-gray-300 ml-2">
                  <svg
                    width="28"
                    height="28"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-700"
                    viewBox="0 0 24 24"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
